import { useState } from 'react';
import { Mail, Lock, Eye, EyeOff, X, Heart, User, AlertCircle } from 'lucide-react';
import { useAuth } from '@/context/AuthContext';
import { toast } from 'sonner';

const LoginModal = ({ isOpen, onClose, onSuccess, context = 'favorites' }) => {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  const [isRegister, setIsRegister] = useState(false);
  const [name, setName] = useState('');
  const [showGoogleConfirm, setShowGoogleConfirm] = useState(false);

  const { login, register, loginWithGoogle } = useAuth();

  if (!isOpen) return null;

  // Context-specific content
  const isFavoritesContext = context === 'favorites';
  const HeaderIcon = isFavoritesContext ? Heart : User;
  const headerTitle = isRegister 
    ? 'Create Account' 
    : isFavoritesContext 
      ? 'Sign In to Save' 
      : 'Sign In to Your Account';
  const headerSubtitle = isRegister
    ? 'Join to access all features'
    : isFavoritesContext
      ? 'Sign in to save favorites across devices'
      : 'Access your profile and saved items';
  const iconBgColor = isFavoritesContext ? 'bg-pink-50' : 'bg-blue-50';
  const iconColor = isFavoritesContext ? 'text-pink-500' : 'text-blue-500';

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);

    try {
      if (isRegister) {
        if (!name.trim()) {
          toast.error('Please enter your name');
          setLoading(false);
          return;
        }
        await register(email, password, name);
        toast.success('Account created successfully!');
      } else {
        await login(email, password);
        toast.success('Welcome back!');
      }
      onClose();
      if (onSuccess) onSuccess();
    } catch (error) {
      toast.error(error.message);
    } finally {
      setLoading(false);
    }
  };

  const handleGoogleClick = () => {
    setShowGoogleConfirm(true);
  };

  const handleGoogleConfirm = () => {
    setShowGoogleConfirm(false);
    loginWithGoogle();
  };

  const resetForm = () => {
    setEmail('');
    setPassword('');
    setName('');
    setShowPassword(false);
    setIsRegister(false);
    setShowGoogleConfirm(false);
  };

  const handleClose = () => {
    resetForm();
    onClose();
  };

  return (
    <div 
      className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4"
      onClick={handleClose}
      data-testid="login-modal-overlay"
    >
      <div 
        className="bg-white rounded-xl max-w-md w-full p-6 shadow-2xl max-h-[90vh] overflow-y-auto"
        onClick={(e) => e.stopPropagation()}
        data-testid="login-modal"
      >
        {/* Header */}
        <div className="flex items-start justify-between mb-6">
          <div className="flex items-center gap-3">
            <div className={`w-10 h-10 rounded-full ${iconBgColor} flex items-center justify-center`}>
              <HeaderIcon className={`w-5 h-5 ${iconColor}`} />
            </div>
            <div>
              <h3 className="font-heading text-xl">
                {headerTitle}
              </h3>
              <p className="text-sm text-site-text-muted">
                {headerSubtitle}
              </p>
            </div>
          </div>
          <button 
            onClick={handleClose}
            className="text-site-text-muted hover:text-site-text transition-colors p-1"
            data-testid="close-login-modal"
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        {/* Google Sign In */}
        <button
          onClick={handleGoogleClick}
          className="w-full flex items-center justify-center gap-3 px-4 py-3 border-2 border-site-text rounded-lg font-mono text-sm uppercase tracking-wide hover:bg-site-bg-alt transition-colors mb-4"
          data-testid="modal-google-login-btn"
        >
          <svg className="w-5 h-5" viewBox="0 0 24 24">
            <path fill="#4285F4" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"/>
            <path fill="#34A853" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"/>
            <path fill="#FBBC05" d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"/>
            <path fill="#EA4335" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"/>
          </svg>
          Continue with Google
        </button>

        {/* Google Confirmation Dialog */}
        {showGoogleConfirm && (
          <div className="mb-4 p-4 bg-site-bg-alt rounded-lg">
            <div className="flex items-center gap-2 mb-3">
              <AlertCircle className="w-4 h-4 text-site-text-muted" />
              <span className="text-sm font-medium">Heads up</span>
            </div>
            <p className="text-sm text-site-text-muted mb-3">
              A new account will be created if you don't have one.
            </p>
            <div className="flex gap-2">
              <button
                onClick={() => setShowGoogleConfirm(false)}
                className="flex-1 py-2 text-sm border border-site-border rounded hover:bg-white transition-colors"
              >
                Cancel
              </button>
              <button
                onClick={handleGoogleConfirm}
                className="flex-1 py-2 text-sm bg-site-bg-dark text-white rounded hover:bg-site-bg-dark/90 transition-colors"
                data-testid="modal-google-confirm-btn"
              >
                Continue
              </button>
            </div>
          </div>
        )}

        {/* Divider */}
        <div className="relative my-4">
          <div className="absolute inset-0 flex items-center">
            <div className="w-full border-t border-site-border"></div>
          </div>
          <div className="relative flex justify-center text-sm">
            <span className="px-3 bg-white text-site-text-muted font-mono text-xs uppercase">
              or with email
            </span>
          </div>
        </div>

        {/* Email/Password Form */}
        <form onSubmit={handleSubmit} className="space-y-3">
          {isRegister && (
            <div>
              <label className="block font-mono text-xs uppercase tracking-widest text-site-text-muted mb-1">
                Name
              </label>
              <input
                type="text"
                value={name}
                onChange={(e) => setName(e.target.value)}
                placeholder="Your name"
                className="w-full px-3 py-2.5 border border-site-border rounded-lg focus:border-site-text focus:outline-none transition-colors text-sm"
                data-testid="modal-name-input"
              />
            </div>
          )}

          <div>
            <label className="block font-mono text-xs uppercase tracking-widest text-site-text-muted mb-1">
              Email
            </label>
            <div className="relative">
              <Mail className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-site-text-muted" />
              <input
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder="you@example.com"
                required
                className="w-full pl-10 pr-3 py-2.5 border border-site-border rounded-lg focus:border-site-text focus:outline-none transition-colors text-sm"
                data-testid="modal-email-input"
              />
            </div>
          </div>

          <div>
            <label className="block font-mono text-xs uppercase tracking-widest text-site-text-muted mb-1">
              Password
            </label>
            <div className="relative">
              <Lock className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-site-text-muted" />
              <input
                type={showPassword ? 'text' : 'password'}
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                placeholder="••••••••"
                required
                minLength={6}
                className="w-full pl-10 pr-10 py-2.5 border border-site-border rounded-lg focus:border-site-text focus:outline-none transition-colors text-sm"
                data-testid="modal-password-input"
              />
              <button
                type="button"
                onClick={() => setShowPassword(!showPassword)}
                className="absolute right-3 top-1/2 -translate-y-1/2 text-site-text-muted hover:text-site-text"
              >
                {showPassword ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
              </button>
            </div>
          </div>

          <button
            type="submit"
            disabled={loading}
            className="w-full py-2.5 bg-site-bg-dark text-white font-mono text-sm uppercase tracking-widest rounded-lg hover:bg-site-bg-dark/90 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
            data-testid="modal-submit-btn"
          >
            {loading ? 'Please wait...' : (isRegister ? 'Create Account' : 'Sign In')}
          </button>
        </form>

        {/* Toggle Register/Login */}
        <p className="text-center mt-4 text-sm text-site-text-muted">
          {isRegister ? 'Already have an account?' : "Don't have an account?"}
          <button
            onClick={() => setIsRegister(!isRegister)}
            className="ml-1 text-site-text font-medium hover:underline"
            data-testid="modal-toggle-auth-btn"
          >
            {isRegister ? 'Sign In' : 'Create Account'}
          </button>
        </p>
      </div>
    </div>
  );
};

export default LoginModal;
